import { format, isToday } from 'date-fns';
import type { CmsArticleState } from '../content/ArticleWorkflow';
import type { ItemCheck, VoiceCheck } from '../reach/voiceWords';
import type { Finding } from './shared';

/**
 * The words on History (design "Version history and rollback"; HistoryView.tsx
 * draws them): each version's approval line, tag and status, its time to the
 * minute, and the word diff behind Compare. The server says what's true of each
 * version (tinycmsHistory, versions.js); nothing here decides it.
 */

export interface HistoryField {
  label: string;
  /** Null: not recorded on a version this old. */
  value: string | null;
}

export interface HistoryApproval {
  type: 'person' | 'rule';
  name?: string | null;
  channel?: string | null;
  rule?: string | null;
  at?: string | null;
}

/** Beyond the fields: an image, a link, a setting. Compare checks these too. */
export interface HistoryDetail {
  label: string;
  value: string;
  /** Too long to compare word by word: value is its hash. */
  opaque?: boolean;
}

export interface HistoryVersion {
  v: number;
  what: string;
  madeBy: { kind: string; name: string; label: string };
  at: string;
  hash: string;
  approval: HistoryApproval | null;
  pending: boolean;
  superseded: boolean;
  sameAsLive: boolean;
  postedAsNew?: boolean;
  partial: boolean;
  restoredFrom: number | null;
  fields: HistoryField[];
  details?: HistoryDetail[];
  /** A post's words, link and picture, for See this version. */
  look?: {
    title?: string | null;
    summary?: string | null;
    postUrl?: string | null;
    imageUrl?: string | null;
  };
}

export interface HistoryData {
  kind: 'article' | 'page' | 'post';
  title: string;
  /** What happened to a page that isn't a version of it: becoming the home page. */
  notes?: Array<{ at: string; what: string; by: string }>;
  channel?: string;
  /** A post's network, and the connected account it posts as (null once disconnected). */
  platform?: string;
  channelProfile?: {
    id: string;
    platform: string;
    profileId?: string | null;
    profileName?: string | null;
    profileUsername?: string | null;
  } | null;
  liveWord: string;
  liveV: number;
  scheduledV: number;
  latestV: number;
  nextV: number;
  posted?: boolean;
  status?: string;
  canApprove?: boolean;
  approve: { action: string; scheduledFor: string | null };
  /** The checks on what's waiting: as an article's or page's state has them, or a post's own. */
  checks?: {
    preflight?: Finding[];
    copyEdit?: CmsArticleState['copyEdit'] | null;
    voice?: VoiceCheck | null;
    items?: ItemCheck | null;
  } | null;
  versions: HistoryVersion[];
}

export const KIND_NAME = { article: 'Article', page: 'Page', post: 'Social post' } as const;

export const shortHash = (hash: string) => String(hash || '').slice(0, 10);

/** See this version: what the preview shows as it was, and what it can't. */
export function seeNote(kind: HistoryData['kind'], v: Pick<HistoryVersion, 'partial'>) {
  if (kind === 'post') {
    return 'How tinycms draws this post. The network’s own page can look a little different.';
  }
  const old = v.partial
    ? ' This version is old: only its title, summary, body and main picture were recorded.'
    : '';
  return `Shown in the site’s design from when it was saved. Article lists, author details and replaced pictures show what’s there today. Links and forms don’t work here.${old}`;
}

/** To the minute, never seconds: "Oct 10, 8:40 AM", "Today, 2:12 PM", "Just now". */
export function minuteTime(at: string | number | Date, now = Date.now()) {
  const d = new Date(typeof at === 'string' && /^\d{10,}$/.test(at) ? Number(at) : at);
  if (Number.isNaN(d.getTime())) return '';
  const ago = now - d.getTime();
  if (ago >= 0 && ago < 60 * 1000) return 'Just now';
  if (isToday(d)) return `Today, ${format(d, 'h:mm a')}`;
  return format(d, 'MMM d, h:mm a');
}

export function approvalLine(v: HistoryVersion, data: HistoryData) {
  if (v.superseded) return { text: 'Replaced before approval', tone: 'muted' };
  if (v.pending) {
    return { text: 'Waiting for approval', tone: 'amber' };
  }
  if (v.approval?.type === 'rule') {
    return { text: `Approved under rule “${v.approval.rule}”`, tone: 'plain' };
  }
  if (v.approval) {
    const chat = v.approval.channel === 'chat' ? ' in chat' : '';
    return {
      text: `Approved by ${v.approval.name || 'someone on your team'}${chat}`,
      tone: 'plain',
    };
  }
  if (v.sameAsLive) return { text: 'The same as the live version', tone: 'muted' };
  if (v.v === data.latestV && v.v > data.liveV) return { text: 'Not approved yet', tone: 'muted' };
  return { text: 'Saved, not published', tone: 'muted' };
}

export function tagOf(v: HistoryVersion, data: HistoryData) {
  if (v.postedAsNew) return { text: data.liveWord, tone: 'green' };
  if (v.v === data.scheduledV) return { text: 'Scheduled', tone: 'green' };
  if (v.v === data.liveV) return { text: data.liveWord, tone: 'green' };
  if (v.pending) return { text: 'Waiting for approval', tone: 'amber' };
  if (v.v === data.latestV && v.v > data.liveV) return { text: 'Working copy', tone: 'neutral' };
  return null;
}

const wentLive = (v: HistoryVersion) => Boolean(v.approval) && !v.pending;

export function statusOf(v: HistoryVersion, data: HistoryData) {
  if (v.postedAsNew) return `Approved. It goes out as a new post on ${data.channel}.`;
  if (v.sameAsLive) return 'This matches what’s live, so there’s nothing to approve.';
  if (v.v === data.liveV) {
    return data.kind === 'post'
      ? `This is what went out on ${data.channel}.`
      : 'Live now. This is what readers see.';
  }
  if (v.v === data.scheduledV) return 'Scheduled. This is what will go out.';
  if (v.pending) return 'Not live yet.';
  if (wentLive(v)) {
    const next =
      data.versions.find((x) => x.v > v.v && wentLive(x))?.v || (data.liveV > v.v ? data.liveV : 0);
    return next ? `Was live until v${next} replaced it.` : 'Approved, not live.';
  }
  if (v.v === data.latestV) return 'Not live yet.';
  return 'Never published.';
}

export function pendingNote(v: HistoryVersion, data: HistoryData) {
  const verb =
    data.kind === 'post'
      ? data.approve.action === 'new_post'
        ? 'posts exactly this version as a new post'
        : data.approve.action === 'replace'
          ? 'schedules exactly this version in place of the one scheduled, at the same time'
          : 'posts exactly this version'
      : data.approve.action === 'schedule'
        ? 'schedules exactly this version, at the same time'
        : 'publishes exactly this version';
  return `v${v.v} is waiting for approval. Approving it ${verb} (${shortHash(v.hash)}).`;
}

export function approveLabel(v: HistoryVersion, data: HistoryData) {
  if (data.kind !== 'post') {
    const how = data.approve.action === 'schedule' ? 'schedule' : 'publish';
    return `Approve and ${how} v${v.v}`;
  }
  return `Approve v${v.v}`;
}

/** The confirm step before a restore. */
export function restoreCopy(x: HistoryVersion, data: HistoryData) {
  const n = data.nextV;
  const first = data.versions[0]?.v ?? 1;
  const parts = [
    `tinycms copies v${x.v} into a new working copy, v${n}. It doesn’t go live on its own: v${n} goes for approval like any other change, and approving it applies exactly that version, nothing edited after.`,
  ];
  if (data.kind === 'post' && data.posted) {
    parts.push(
      `${data.channel} doesn’t allow editing a post after it goes out, so once approved, v${n} goes out as a new post. The original stays up.`
    );
  } else if (data.scheduledV) {
    parts.push(`Once approved, it replaces v${data.scheduledV} at the same scheduled time.`);
  } else if (data.liveV && data.kind !== 'post') {
    parts.push(`Readers keep seeing v${data.liveV} until then.`);
  }
  if (x.partial) {
    parts.push(
      'This version is too old to have its tags, meta description and hero alt text, so those stay as they are.'
    );
  }
  parts.push(`Nothing is deleted: v${first}–v${data.latestV} stay in history.`);
  return {
    title: `Restore v${x.v} as a new working copy?`,
    body: parts.join(' '),
    go: `Restore as v${n}`,
  };
}

export const versionOption = (v: HistoryVersion, data: HistoryData) =>
  `v${v.v}${
    v.v === data.liveV
      ? ` · ${data.liveWord.toLowerCase()}`
      : v.v === data.scheduledV
        ? ' · scheduled'
        : v.pending
          ? ' · waiting'
          : v.v === data.latestV && v.v > data.liveV
            ? ' · working copy'
            : ''
  } · ${v.what}`;

// ── Compare ──────────────────────────────────────────────────────────────────

export type DiffSeg = { k: 'same' | 'add' | 'del'; t: string };

// A longest-common-subsequence table this big would stall the tab: past it, the
// changed stretch shows as one removal and one addition.
const MAX_DIFF_CELLS = 4_000_000;

export function wordDiff(a: string, b: string): DiffSeg[] {
  const tok = (x: string) =>
    String(x)
      .split(/(\s+)/)
      .filter((y) => y !== '');
  const A = tok(a);
  const B = tok(b);
  let start = 0;
  while (start < A.length && start < B.length && A[start] === B[start]) start++;
  let endA = A.length;
  let endB = B.length;
  while (endA > start && endB > start && A[endA - 1] === B[endB - 1]) {
    endA--;
    endB--;
  }
  const out: DiffSeg[] = [];
  const push = (k: DiffSeg['k'], t: string) => {
    const last = out[out.length - 1];
    if (last && last.k === k) last.t += t;
    else if (t) out.push({ k, t });
  };
  push('same', A.slice(0, start).join(''));
  const midA = A.slice(start, endA);
  const midB = B.slice(start, endB);
  const n = midA.length;
  const m = midB.length;
  if (n * m > MAX_DIFF_CELLS) {
    push('del', midA.join(''));
    push('add', midB.join(''));
  } else {
    const dp: Uint32Array[] = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
    for (let i = n - 1; i >= 0; i--) {
      for (let j = m - 1; j >= 0; j--) {
        dp[i][j] =
          midA[i] === midB[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
    let i = 0;
    let j = 0;
    while (i < n && j < m) {
      if (midA[i] === midB[j]) {
        push('same', midA[i]);
        i++;
        j++;
      } else if (dp[i + 1][j] >= dp[i][j + 1]) {
        push('del', midA[i++]);
      } else {
        push('add', midB[j++]);
      }
    }
    while (i < n) push('del', midA[i++]);
    while (j < m) push('add', midB[j++]);
  }
  push('same', A.slice(endA).join(''));
  return out;
}

const wordCount = (segs: DiffSeg[], k: DiffSeg['k']) =>
  segs.filter((s) => s.k === k).reduce((n, s) => n + s.t.split(/\s+/).filter(Boolean).length, 0);

/**
 * Two versions, field by field. A field only one of them has counts as empty on
 * the other (a page's block added later); one an older version didn't record
 * can't be compared, so it's listed apart rather than shown as a change. Details
 * are what a version holds beyond its fields: only a difference is worth a line.
 */
export function compareVersions(from: HistoryVersion, to: HistoryVersion) {
  const a = new Map(from.fields.map((f) => [f.label, f.value]));
  const b = new Map(to.fields.map((f) => [f.label, f.value]));
  const labels = [
    ...new Set([...from.fields.map((f) => f.label), ...to.fields.map((f) => f.label)]),
  ];
  const same: string[] = [];
  const unknown: string[] = [];
  let added = 0;
  let removed = 0;
  const changed = labels.flatMap((label) => {
    const x = a.get(label);
    const y = b.get(label);
    if (x === null || y === null) {
      unknown.push(label);
      return [];
    }
    if ((x ?? '') === (y ?? '')) {
      same.push(label);
      return [];
    }
    const segs = wordDiff(x ?? '', y ?? '');
    const ad = wordCount(segs, 'add');
    const dl = wordCount(segs, 'del');
    added += ad;
    removed += dl;
    const stat =
      [ad ? `${ad} ${ad === 1 ? 'word' : 'words'} added` : '', dl ? `${dl} removed` : '']
        .filter(Boolean)
        .join(' · ') || 'Changed';
    return [{ label, stat, segs }];
  });
  if (!from.partial && !to.partial) {
    const was = new Map((from.details || []).map((d) => [d.label, d]));
    const now = new Map((to.details || []).map((d) => [d.label, d]));
    for (const label of new Set([...was.keys(), ...now.keys()])) {
      const x = was.get(label);
      const y = now.get(label);
      if (x?.value === y?.value) continue;
      const segs = x?.opaque || y?.opaque ? [] : wordDiff(x?.value ?? '', y?.value ?? '');
      changed.push({ label, stat: 'Changed', segs });
    }
  }
  return { changed, same, unknown, added, removed };
}

/**
 * When Compare has no line to show. "Matches exactly" only when the two hashes
 * are the same: a different hash means something differs, whatever we can show.
 */
export function noDifferenceLine(from: HistoryVersion, to: HistoryVersion, unknown: string[] = []) {
  if (from.hash === to.hash) return `No differences: v${to.v} matches v${from.v} exactly.`;
  return unknown.length
    ? `v${to.v} and v${from.v} aren’t identical, but the older one doesn’t record everything, so we can’t show what changed.`
    : `v${to.v} and v${from.v} aren’t identical, but what changed isn’t something we can show here, such as formatting or an empty setting.`;
}
